/**
 * @file ProductRatesCard.tsx
 * @brief Per-product list-rate editor — one currency at a time + live sample.
 *
 * @details
 * Diff-only save so untouched keys keep honest `updated_at`. Currency tabs
 * replace the old 3-column wall (30-07-2026). Live sample shows a typical
 * session total for the active currency as you type.
 *
 * @author DES
 * @date 05-07-2026
 * @updated 30-07-2026 — currency tabs + live sample
 */

import { useEffect, useMemo, useState } from "react";
import { LoaderCircle, type LucideIcon, Save } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { CountryFlag } from "@/components/ui/CountryFlag";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import {
  GridBand,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { useUpdateSettings, type UpdateSettingsInput } from "../api";
import type { SystemSettingsResponse } from "../schemas";

const CURRENCIES = [
  { code: "GBP", symbol: "£", label: "British Pound", flag: "gb" },
  { code: "USD", symbol: "$", label: "US Dollar", flag: "us" },
  { code: "EUR", symbol: "€", label: "Euro", flag: "eu" },
] as const;

type CurrencyCode = (typeof CURRENCIES)[number]["code"];

function minorToMajor(stored: string | undefined): string {
  if (!stored) return "";
  const n = Number(stored);
  if (Number.isNaN(n)) return "";
  return (n / 100).toFixed(2);
}

function majorToMinor(input: string): number {
  const n = Number(input);
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}

function formatMoney(minor: number, symbol: string): string {
  return `${symbol}${(minor / 100).toFixed(2)}`;
}

export interface ProductRatesCardProps {
  data: SystemSettingsResponse | undefined;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Per-minute rate key. Omit when `fixedKey` is set (fixed-price products). */
  rateKey?: (code: CurrencyCode) => string;
  minKey?: (code: CurrencyCode) => string;
  speakerFeeKey?: (code: CurrencyCode) => string;
  /** Fixed report/list price key (Medical Assessment english_recording). */
  fixedKey?: (code: CurrencyCode) => string;
  /** Hide product header when parent already shows product switcher. */
  embedded?: boolean;
  /** Controlled currency tab (parent owns switcher across products). */
  currency?: CurrencyCode;
  onCurrencyChange?: (code: CurrencyCode) => void;
}

export function ProductRatesCard({
  data,
  title,
  description,
  icon: Icon,
  rateKey,
  minKey,
  speakerFeeKey,
  fixedKey,
  embedded = false,
  currency: currencyProp,
  onCurrencyChange,
}: ProductRatesCardProps) {
  const settings = data?.settings ?? {};
  const [internalCurrency, setInternalCurrency] = useState<CurrencyCode>("GBP");
  const activeCurrency = currencyProp ?? internalCurrency;
  const setActiveCurrency = (code: CurrencyCode) => {
    onCurrencyChange?.(code);
    if (currencyProp == null) setInternalCurrency(code);
  };

  const [rates, setRates] = useState<Record<string, string>>({});
  const [mins, setMins] = useState<Record<string, string>>({});
  const [speakerFees, setSpeakerFees] = useState<Record<string, string>>({});
  const [fixedPrices, setFixedPrices] = useState<Record<string, string>>({});

  useEffect(() => {
    const nextRates: Record<string, string> = {};
    const nextMins: Record<string, string> = {};
    const nextFees: Record<string, string> = {};
    const nextFixed: Record<string, string> = {};
    for (const c of CURRENCIES) {
      if (rateKey) nextRates[c.code] = minorToMajor(settings[rateKey(c.code)]?.value);
      if (minKey) nextMins[c.code] = minorToMajor(settings[minKey(c.code)]?.value);
      if (speakerFeeKey)
        nextFees[c.code] = minorToMajor(settings[speakerFeeKey(c.code)]?.value);
      if (fixedKey)
        nextFixed[c.code] = minorToMajor(settings[fixedKey(c.code)]?.value);
    }
    setRates(nextRates);
    setMins(nextMins);
    setSpeakerFees(nextFees);
    setFixedPrices(nextFixed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const update = useUpdateSettings();

  const dirtyPayload = useMemo<UpdateSettingsInput | null>(() => {
    const patch: Record<string, string | number> = {};

    for (const c of CURRENCIES) {
      if (fixedKey) {
        const fKey = fixedKey(c.code);
        const newFixed = majorToMinor(fixedPrices[c.code] ?? "0");
        const storedFixed = Number(settings[fKey]?.value ?? 0);
        if (newFixed !== storedFixed) patch[fKey] = newFixed;
        continue;
      }

      if (rateKey) {
        const rKey = rateKey(c.code);
        const newRate = majorToMinor(rates[c.code] ?? "0");
        const storedRate = Number(settings[rKey]?.value ?? 0);
        if (newRate !== storedRate) patch[rKey] = newRate;
      }

      if (minKey) {
        const mKey = minKey(c.code);
        const newMin = majorToMinor(mins[c.code] ?? "0");
        const storedMin = Number(settings[mKey]?.value ?? 0);
        if (newMin !== storedMin) patch[mKey] = newMin;
      }

      if (speakerFeeKey) {
        const sKey = speakerFeeKey(c.code);
        const newFee = majorToMinor(speakerFees[c.code] ?? "0");
        const storedFee = Number(settings[sKey]?.value ?? 0);
        if (newFee !== storedFee) patch[sKey] = newFee;
      }
    }

    return Object.keys(patch).length > 0 ? { settings: patch } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates, mins, speakerFees, fixedPrices, settings]);

  const activeMeta =
    CURRENCIES.find((c) => c.code === activeCurrency) ?? CURRENCIES[0];

  const sample = useMemo(() => {
    if (fixedKey) {
      const fixed = majorToMinor(fixedPrices[activeCurrency] ?? "0");
      return {
        sampleMinutes: 0,
        sampleSpeakers: 0,
        total: fixed,
        floored: false,
        symbol: activeMeta.symbol,
        line: `Fixed report price`,
      };
    }
    const rate = majorToMinor(rates[activeCurrency] ?? "0");
    const minCharge = minKey
      ? majorToMinor(mins[activeCurrency] ?? "0")
      : 0;
    const speakers = speakerFeeKey
      ? majorToMinor(speakerFees[activeCurrency] ?? "0")
      : 0;
    const sampleMinutes = 30;
    const sampleSpeakers = speakerFeeKey ? 2 : 0;
    const raw =
      sampleMinutes * rate + sampleSpeakers * speakers;
    const total = Math.max(raw, minCharge);
    const floored = minCharge > 0 && raw < minCharge;
    return {
      sampleMinutes,
      sampleSpeakers,
      total,
      floored,
      symbol: activeMeta.symbol,
      line: speakerFeeKey
        ? `${sampleMinutes} min × ${formatMoney(rate, activeMeta.symbol)}/min + ${sampleSpeakers} speakers × ${formatMoney(speakers, activeMeta.symbol)}`
        : `${sampleMinutes} min × ${formatMoney(rate, activeMeta.symbol)}/min`,
    };
  }, [
    rates,
    mins,
    speakerFees,
    fixedPrices,
    activeCurrency,
    activeMeta.symbol,
    minKey,
    speakerFeeKey,
    fixedKey,
  ]);

  const handleSave = async () => {
    if (!dirtyPayload) return;
    try {
      await update.mutateAsync(dirtyPayload);
      toast({
        variant: "success",
        title: `${title} updated`,
        description: `Saved ${Object.keys(dirtyPayload.settings).length} field(s).`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <>
      {!embedded ? (
        <GridBand className="flex flex-wrap items-center justify-between gap-3 py-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-base font-semibold tracking-tight">
              <Icon className="h-4 w-4 text-primary" />
              {title}
            </h3>
            <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
          </div>
        </GridBand>
      ) : null}

      <GridCell>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div
              // The phone's pill track (DES 08-10-2026, every desktop tab row).
              className="inline-flex items-center gap-1 rounded-full bg-[#ECEBE9] p-1.5 dark:bg-app-quaternary"
              role="tablist"
              aria-label="Currency"
            >
              {CURRENCIES.map((c) => {
                const active = activeCurrency === c.code;
                return (
                  <button
                    key={c.code}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    title={c.label}
                    onClick={() => setActiveCurrency(c.code)}
                    className={cn(
                      "inline-flex h-[38px] min-w-[7.5rem] items-center justify-center gap-2 rounded-full px-4 text-[13px] font-semibold transition-colors focus-visible:outline-none",
                      active
                        ? "bg-[#E2E0DD] text-foreground dark:bg-white/10"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <CountryFlag code={c.flag} size="sm" className="h-4 w-4" />
                    <span className="tracking-wide">{c.code}</span>
                    <span
                      className={cn(
                        "tabular-nums",
                        active ? "text-muted-foreground" : "text-muted-foreground/80",
                      )}
                    >
                      {c.symbol}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {dirtyPayload
                  ? `${Object.keys(dirtyPayload.settings).length} pending`
                  : "Saved"}
              </span>
              <Button
                size="sm"
                onClick={() => void handleSave()}
                disabled={!dirtyPayload || update.isPending}
              >
                {update.isPending ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" /> Save {title}
                  </>
                )}
              </Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Editing <strong className="text-foreground">{activeMeta.code}</strong>{" "}
            ({activeMeta.label}). Switch tabs to set other currencies.
          </p>

          <div className="grid max-w-xl gap-3 sm:grid-cols-2">
            {fixedKey ? (
              <div className="sm:col-span-2 sm:max-w-xs">
                <FormFieldGroup
                  label={`Fixed report price (${activeMeta.symbol})`}
                  htmlFor={`fixed-${title}-${activeCurrency}`}
                >
                  <Input
                    id={`fixed-${title}-${activeCurrency}`}
                    inputMode="decimal"
                    placeholder="6.00"
                    value={fixedPrices[activeCurrency] ?? ""}
                    onChange={(e) =>
                      setFixedPrices({
                        ...fixedPrices,
                        [activeCurrency]: e.target.value,
                      })
                    }
                  />
                </FormFieldGroup>
              </div>
            ) : (
              <>
                <FormFieldGroup
                  label={`Per minute (${activeMeta.symbol})`}
                  htmlFor={`rate-${title}-${activeCurrency}`}
                >
                  <Input
                    id={`rate-${title}-${activeCurrency}`}
                    inputMode="decimal"
                    placeholder="0.20"
                    value={rates[activeCurrency] ?? ""}
                    onChange={(e) =>
                      setRates({ ...rates, [activeCurrency]: e.target.value })
                    }
                  />
                </FormFieldGroup>
                {minKey ? (
                  <FormFieldGroup
                    label={`Min charge (${activeMeta.symbol})`}
                    htmlFor={`min-${title}-${activeCurrency}`}
                  >
                    <Input
                      id={`min-${title}-${activeCurrency}`}
                      inputMode="decimal"
                      placeholder="0.30"
                      value={mins[activeCurrency] ?? ""}
                      onChange={(e) =>
                        setMins({ ...mins, [activeCurrency]: e.target.value })
                      }
                    />
                  </FormFieldGroup>
                ) : null}
                {speakerFeeKey ? (
                  <div className="sm:col-span-2 sm:max-w-xs">
                    <FormFieldGroup
                      label={`Per speaker (${activeMeta.symbol})`}
                      htmlFor={`fee-${title}-${activeCurrency}`}
                    >
                      <Input
                        id={`fee-${title}-${activeCurrency}`}
                        inputMode="decimal"
                        placeholder="0.50"
                        value={speakerFees[activeCurrency] ?? ""}
                        onChange={(e) =>
                          setSpeakerFees({
                            ...speakerFees,
                            [activeCurrency]: e.target.value,
                          })
                        }
                      />
                    </FormFieldGroup>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="rounded-md border border-border/80 bg-muted/20 px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Sample ({activeMeta.code})
            </p>
            <p className="mt-1 text-sm text-foreground">
              {sample.line}
              {sample.floored ? " → min charge applied" : ""}
            </p>
            <p className="mt-0.5 text-base font-semibold tabular-nums">
              {formatMoney(sample.total, sample.symbol)}
            </p>
          </div>
        </div>
      </GridCell>
    </>
  );
}

// `CURRENCIES` is deliberately NOT re-exported: it had no consumer, and a value
// export next to `ProductRatesCard` costs the module its Fast Refresh. The type
// alias below is fine — types are erased, so they don't affect it. (02-08-2026)
export type { CurrencyCode as ProductRateCurrencyCode };
